import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ship, Mail, Lock, User, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1678666716714-e5f095f0ae4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.access_token, data.user);
      toast.success("Account created! Welcome aboard.");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <span className="h-9 w-9 rounded-md bg-primary flex items-center justify-center"><Ship className="h-5 w-5 text-white" /></span>
            <span className="font-display font-bold text-lg text-primary">WaveLine</span>
          </Link>
          <h1 className="font-display font-bold text-2xl text-primary tracking-tight">Create your account</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Book ferries in seconds</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="pl-9" placeholder="Priya Sharma" data-testid="register-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="pl-9" placeholder="you@example.com" data-testid="register-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="pl-9" placeholder="9876543210" data-testid="register-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} className="pl-9" placeholder="At least 6 characters" data-testid="register-password" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white font-semibold" disabled={loading} data-testid="register-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account? <Link to="/login" className="text-accent font-semibold" data-testid="go-login-link">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO} alt="Ferry" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Ship className="h-8 w-8 text-accent mb-4" />
          <h2 className="font-display font-bold text-3xl tracking-tight">Every coast, one account.</h2>
          <p className="text-white/75 mt-2">Instant e-tickets, saved trips and secure payments.</p>
        </div>
      </div>
    </div>
  );
}
